import type React from "react";
import { useState } from "react";

import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { TagSelect } from "@/components/TagSelect/TagSelect";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

interface EventSignUpWithGuestsModalProps extends ModalBaseProps {
  eventName: string;
  maxGuests: number;
  isLoading: boolean;
  onConfirm: (guestCount: number) => void;
}

export function EventSignUpWithGuestsModal({
  eventName,
  isOpened,
  onOpenChange,
  maxGuests,
  isLoading,
  onConfirm,
}: EventSignUpWithGuestsModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [guests, setGuests] = useState(0);

  const options = Array.from({ length: maxGuests }, (_, i) => ({
    label: t("model.event.details.participants.guest-count", { count: i + 1 }),
    value: i.toString(),
  }));

  const handleConfirm = () => {
    onConfirm(guests);
  };

  return (
    <Modal.Root
      title={t("page.event.signup-with-guests.title")}
      description={eventName}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      size="sm"
      data-testid="event-sign-up-modal"
    >
      <Modal.Body>
        <Stack gap="sm">
          <label id="guests-label" className="text-caption-emphasis text-text-primary">
            {t("page.event.signup-with-guests.guest-count-label")}
          </label>
          <TagSelect
            items={options}
            values={[guests.toString()]}
            onChange={(value) => {
              if (value.length > 0) {
                setGuests(Number(value[0]));
              }
            }}
            aria-labelledby="guest-label"
          />
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="filled" data-testid="confirm-btn" onClick={handleConfirm} isLoading={isLoading}>
          {guests === 0
            ? t("page.event.signup-with-guests.confirm-alone")
            : t("page.event.signup-with-guests.confirm-with-guests", { count: guests || 0 })}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
